import type { LostReceiptDeclaration, PolicyViolation } from "@app/types/accounting";

export const VIOLATION_ACTION_REQUIRE_EXPLANATION = "requireExplanation";
export const VIOLATION_STATUS_ACTIVE = "active";
export const VIOLATION_STATUS_RESOLVED = "resolved";
export const VIOLATION_STATUS_JUSTIFIED = "justified";
export const VIOLATION_POLICY_TYPE_RECEIPT = "receipt";

export const ITEM_VIOLATION_APPROVE_TOOLTIP = "This expense has a policy violation that must be resolved by the submitter before it can be approved.";

export const CANNOT_APPROVE_COPY = {
    title: "Cannot Approve Report",
    explanation: "One or more expense items have policy violations that require a written explanation from the submitter before this report can be approved.",
    receipt: "One or more expense items require a receipt to be attached before this report can be approved.",
    resolution: "Send the report back to the submitter to resolve before approving.",
} as const;

/** Minimal expense shape needed to evaluate policy violations; satisfied by list, report and full-detail items. */
export interface ViolationEvaluableExpense {
    violations?: PolicyViolation[] | null;
    receiptMissing: boolean;
    lostReceiptDeclaration?: LostReceiptDeclaration | null;
}

export interface ApprovalBlockingKinds {
    explanation: boolean;
    receipt: boolean;
}

export type ReceiptIndicatorKind = "lostDeclared" | "uploaded" | "required" | "notRequired";

export type ViolationPopupTone = "required" | "warning";

// Mirrors WebV5's getActiveViolations: every status except "resolved" stays relevant (active/justified/frozen).
export function getActiveViolations(violations: PolicyViolation[] | null | undefined): PolicyViolation[] {
    return (violations ?? []).filter((violation) => violation.status !== VIOLATION_STATUS_RESOLVED);
}

export function violationRequiresExplanation(violation: PolicyViolation): boolean {
    return Array.isArray(violation.actions) && violation.actions.some((action) => action.type === VIOLATION_ACTION_REQUIRE_EXPLANATION);
}

// Whitespace-only justifications count as empty; a "justified" status counts as explained.
export function violationNeedsJustification(violation: PolicyViolation): boolean {
    return violationRequiresExplanation(violation) && !violationHasExplanation(violation);
}

// The submitter has explained the violation: it is justified or carries a non-empty justification.
export function violationHasExplanation(violation: PolicyViolation): boolean {
    return String(violation.status).toLowerCase() === VIOLATION_STATUS_JUSTIFIED || !!violation.justification?.trim();
}

export function hasReceiptViolation(violations: PolicyViolation[] | null | undefined): boolean {
    return getActiveViolations(violations).some((violation) => violation.policyType === VIOLATION_POLICY_TYPE_RECEIPT);
}

// Receipt violations are surfaced by the receipt indicator, not by the Exception column.
export function getNonReceiptViolations(violations: PolicyViolation[] | null | undefined): PolicyViolation[] {
    return getActiveViolations(violations).filter((violation) => violation.policyType !== VIOLATION_POLICY_TYPE_RECEIPT);
}

export function hasBlockingExplanationViolation(expense: ViolationEvaluableExpense): boolean {
    return getNonReceiptViolations(expense.violations).some(violationNeedsJustification);
}

// A receipt violation only blocks while no receipt is attached and no lost-receipt declaration exists.
export function hasBlockingReceiptViolation(expense: ViolationEvaluableExpense): boolean {
    return hasReceiptViolation(expense.violations) && expense.receiptMissing && !expense.lostReceiptDeclaration?.declared;
}

export function hasBlockingViolation(expense: ViolationEvaluableExpense): boolean {
    return hasBlockingExplanationViolation(expense) || hasBlockingReceiptViolation(expense);
}

export function getApprovalBlockingKinds(expenses: ViolationEvaluableExpense[]): ApprovalBlockingKinds {
    return {
        explanation: expenses.some(hasBlockingExplanationViolation),
        receipt: expenses.some(hasBlockingReceiptViolation),
    };
}

/** Body paragraphs of the "Cannot Approve Report" popup: explanation first, receipt next, resolution sentence last. */
export function buildCannotApproveParagraphs(kinds: ApprovalBlockingKinds): string[] {
    const messages: string[] = [];
    if (kinds.explanation) messages.push(CANNOT_APPROVE_COPY.explanation);
    if (kinds.receipt) messages.push(CANNOT_APPROVE_COPY.receipt);
    if (messages.length === 0) return [];
    messages[messages.length - 1] = `${messages[messages.length - 1]} ${CANNOT_APPROVE_COPY.resolution}`;
    return messages;
}

// Priority: lost-receipt declaration, then an attached receipt, then a receipt violation, then "not required".
export function getReceiptIndicator(expense: ViolationEvaluableExpense): { kind: ReceiptIndicatorKind; label: string } {
    if (expense.lostReceiptDeclaration?.declared) return { kind: "lostDeclared", label: "Lost receipt declared" };
    if (!expense.receiptMissing) return { kind: "uploaded", label: "Receipt uploaded" };
    if (hasReceiptViolation(expense.violations)) return { kind: "required", label: "Receipt required" };
    return { kind: "notRequired", label: "Receipt not required" };
}

// Only active, non-receipt violations interrupt the approver after a save (receipt is enforced at approval time).
export function getPopupViolations(violations: PolicyViolation[] | null | undefined): PolicyViolation[] {
    return (violations ?? []).filter(
        (violation) => String(violation.status).toLowerCase() === VIOLATION_STATUS_ACTIVE && violation.policyType !== VIOLATION_POLICY_TYPE_RECEIPT,
    );
}

export function getViolationPopupTone(violations: PolicyViolation[]): ViolationPopupTone {
    return violations.some(violationRequiresExplanation) ? "required" : "warning";
}
